import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  apiKybStatus,
  apiKybBootstrap,
  apiKybStore,
} from "@/lib/api";
import { ApiError } from "@/lib/api";
import KybDocBlock from "@/components/KybDocBlock";
import { DOC_LABELS, type KybDocType } from "@/lib/domains/kybDocs";

type CompanyForm = {
  company_name: string;
  registration_number: string; // coerced to string for display
  incorporation_country: string;
  website_url?: string;
  business_activity: string;
};

const REQUIRED_ON_CREATE: KybDocType[] = [
  "certificate",
  "articles",
  "shareholders",
  "address_proof",
];

const ALL_TYPES: KybDocType[] = [
  "certificate",
  "articles",
  "shareholders",
  "address_proof",
  "vat_certificate",
  "business_license",
];

// Coerce first present value (string or number) to a trimmed string
function pickToString(...vals: Array<string | number | null | undefined>): string {
  for (const v of vals) {
    if (v !== null && v !== undefined) {
      const s = String(v).trim();
      if (s.length > 0) return s;
    }
  }
  return "";
}

export default function KybCreate() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sourceNote, setSourceNote] =
    useState<"kyb" | "status+bootstrap" | "bootstrap" | null>(null);

  const [form, setForm] = useState<CompanyForm>({
    company_name: "",
    registration_number: "",
    incorporation_country: "",
    website_url: "",
    business_activity: "",
  });

  const [files, setFiles] = useState<Partial<Record<KybDocType, File | null>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch both so we can merge — status often lacks company_number but bootstrap has it.
        const [statusRes, bootRes] = await Promise.allSettled([
          apiKybStatus(),
          apiKybBootstrap(),
        ]);

        if (cancelled) return;

        const status =
          statusRes.status === "fulfilled" ? statusRes.value : undefined;
        const boot =
          bootRes.status === "fulfilled" ? bootRes.value : undefined;

        const k = status?.kyb || null;
        const b = status?.businessData || null;

        // Merge order: KYB row -> businessData (status) -> bootstrap
        const company_name = pickToString(
          k?.company_name,
          b?.company_name,
          boot?.company_name
        );

        const registration_number = pickToString(
          // KYB
          (k as any)?.registration_number,
          // businessData (status) — include all common aliases
          (b as any)?.registration_number,
          (b as any)?.company_registration_number,
          (b as any)?.company_registration,
          (b as any)?.company_reg_number,
          (b as any)?.company_reg_no,
          (b as any)?.registration_no,
          (b as any)?.company_number,
          (b as any)?.company_no,
          (b as any)?.reg_number,
          (b as any)?.crn,
          // bootstrap — include same aliases (Blade uses company_number)
          (boot as any)?.registration_number,
          (boot as any)?.company_registration_number,
          (boot as any)?.company_registration,
          (boot as any)?.company_reg_number,
          (boot as any)?.company_reg_no,
          (boot as any)?.registration_no,
          (boot as any)?.company_number,
          (boot as any)?.company_no,
          (boot as any)?.reg_number,
          (boot as any)?.crn
        );

        const incorporation_country = pickToString(
          k?.incorporation_country,
          b?.incorporation_country,
          b?.incorporation_country_code,
          b?.incorporation_country_name,
          b?.country,
          boot?.incorporation_country,
          (boot as any)?.incorporation_country_code,
          (boot as any)?.incorporation_country_name,
          (boot as any)?.country
        );

        const website_url = pickToString(k?.website_url);
        const business_activity = pickToString(k?.business_activity);

        setForm((f) => ({
          ...f,
          company_name,
          registration_number,
          incorporation_country,
          website_url,
          business_activity,
        }));

        // Note which sources contributed
        if (k) setSourceNote("kyb");
        else if (b || boot) setSourceNote(b ? "status+bootstrap" : "bootstrap");
      } catch (e: unknown) {
        const msg = e instanceof ApiError ? e.message : "Failed to load company info.";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const canSubmit = useMemo(() => {
    const hasCoreDocs = REQUIRED_ON_CREATE.every((t) => !!files[t]);
    return !!form.business_activity && hasCoreDocs;
  }, [form.business_activity, files]);

  function update<K extends keyof CompanyForm>(k: K, v: CompanyForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) {
      setError("Please complete the required fields and core documents.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const fd = new FormData();
      if (form.website_url) fd.append("website_url", String(form.website_url));
      fd.append("business_activity", String(form.business_activity));

      ALL_TYPES.forEach((t) => {
        const f = files[t];
        if (f) fd.append(`documents[${t}]`, f);
      });

      await apiKybStore(fd);
      nav("/profile/ubos/create");
    } catch (e: any) {
      if (e instanceof ApiError && e.validation) {
        const first = (Object.values(e.validation)[0] as string[] | undefined)?.[0];
        setError(first || e.message);
      } else {
        setError(e?.message || "Failed to submit KYB.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6 pb-28 sm:pb-6">
        <div className="section-box skeleton h-10 mb-3 rounded-xl" />
        <div className="card-box skeleton h-40 mb-3 rounded-xl" />
        <div className="card-box skeleton h-80 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6 pb-28 sm:pb-6">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-semibold">Company details</h1>
        <Link to="/kyb/status" className="btn btn-white">
          ← Back to KYB
        </Link>
      </header>

      {sourceNote && (
        <div className="alert-info mb-4">
          Prefilled from{" "}
          <strong>
            {sourceNote === "kyb"
              ? "existing KYB record"
              : sourceNote === "status+bootstrap"
              ? "business profile (status + bootstrap)"
              : "business profile (bootstrap)"}
          </strong>.
        </div>
      )}

      {error && <div className="alert-danger mb-4">{error}</div>}

      <form onSubmit={submit} className="space-y-6">
        {/* Company (read-only identity) */}
        <div className="card-box p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block">
              <span className="block text-sm font-medium text-gray-900">
                Company name
              </span>
              <input
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 bg-gray-100"
                value={form.company_name}
                readOnly
                aria-readonly
                placeholder="—"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-900">
                Registration number
              </span>
              <input
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 bg-gray-100"
                value={form.registration_number}
                readOnly
                aria-readonly
                placeholder="—"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-900">
                Country of incorporation
              </span>
              <input
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 bg-gray-100"
                value={form.incorporation_country}
                readOnly
                aria-readonly
                placeholder="—"
                autoComplete="country-name"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-900">
                Website (optional)
              </span>
              <input
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                value={form.website_url || ""}
                onChange={(e) => update("website_url", e.target.value)}
                placeholder="https://example.com"
                type="url"
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-sm font-medium text-gray-900">
              Business activity
            </span>
            <textarea
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              rows={4}
              required
              value={form.business_activity}
              onChange={(e) => update("business_activity", e.target.value)}
              placeholder="Describe the nature of business, products/services, target markets, etc."
            />
          </label>
        </div>

        {/* Documents */}
        <div className="card-box p-4 sm:p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
          <p className="text-sm text-gray-600">
            PDF is preferred; images are accepted for proofs. On first
            submission you may send business details and documents together.
          </p>

          {ALL_TYPES.map((t) => (
            <KybDocBlock
              key={t}
              docType={t}
              allowUpload={true}
              onPick={(type, file) => setFiles((m) => ({ ...m, [type]: file }))}
            />
          ))}

          {!REQUIRED_ON_CREATE.every((t) => files[t]) && (
            <div className="text-xs text-red-600">
              Required on first submission:{" "}
              {REQUIRED_ON_CREATE.map((t) => DOC_LABELS[t]).join(", ")}.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <Link to="/kyb/status" className="btn btn-white">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving || !canSubmit}
            className="btn disabled:opacity-60"
          >
            {saving ? "Submitting…" : "Submit KYB"}
          </button>
        </div>
      </form>
    </div>
  );
}
